import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/AppShell';
import { RequireAuth } from './components/RequireAuth';
import { useSession } from './lib/session';
import { useOffline } from './lib/offline';
import { AccountsPage } from './pages/AccountsPage';
import { BudgetsPage } from './pages/BudgetsPage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { CopilotPage } from './pages/CopilotPage';
import { DashboardPage } from './pages/DashboardPage';
import { DebtsPage } from './pages/DebtsPage';
import { FiadoPage } from './pages/FiadoPage';
import { GoalsPage } from './pages/GoalsPage';
import { ImportPage } from './pages/ImportPage';
import { LoginPage } from './pages/LoginPage';
import { MovementsPage } from './pages/MovementsPage';
import { NewEntryPage } from './pages/NewEntryPage';
import { ReceiptsPage } from './pages/ReceiptsPage';
import { RecurringPage } from './pages/RecurringPage';
import { RegisterPage } from './pages/RegisterPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SplitsPage } from './pages/SplitsPage';

/**
 * Pantallas que todavía no tienen su módulo construido.
 *
 * Quedan tres, y dos de ellas dependen de decisiones pendientes: la lectura de
 * recibos (dónde se procesa la foto) y el copiloto (reglas propias o un modelo de
 * lenguaje). Se publican como «en construcción» en lugar de esconderlas, para que
 * se sepa exactamente qué falta y por qué.
 */
/**
 * Pantallas que todavía no tienen su módulo construido.
 *
 * Ya no queda ninguna: **todas las secciones están hechas**. Se deja la constante para
 * que añadir una pantalla futura sea trivial, y porque el armazón la usa.
 */
const PENDING_ROUTES: ReadonlyArray<{ path: string; title: string; detail: string }> = [];

export function App(): React.JSX.Element {
  const status = useSession((s) => s.status);
  const stealth = useSession((s) => s.stealth);
  const init = useSession((s) => s.init);
  const initOffline = useOffline((s) => s.init);
  const stopOffline = useOffline((s) => s.stop);

  // Al arrancar se intenta recuperar la sesión con la cookie HttpOnly.
  useEffect(() => {
    void init();
  }, [init]);

  // El motor sin conexión arranca con la aplicación y se detiene al cerrarla.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;

    void initOffline();
    return () => stopOffline();
  }, [status, initOffline, stopOffline]);

  // En el teléfono se engancha lo que el navegador no puede dar: el estado real de
  // la conexión, el botón de atrás de Android y volver a primer plano.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;

    let cleanup: (() => void | Promise<void>) | undefined;

    void (async () => {
      const { setupNativeApp } = await import('./lib/native');
      cleanup = await setupNativeApp({
        onConnectivityChange: (online) => {
          // El sistema operativo sabe de la conexión antes que el navegador. Es lo
          // que hace que los movimientos pendientes salgan en cuanto vuelve la red.
          useOffline.setState({ online });
          if (online) void useOffline.getState().sync();
        },
        onResume: () => {
          if (navigator.onLine) void useOffline.getState().sync();
        },
      });
    })();

    return () => {
      void cleanup?.();
    };
  }, [status]);

  // El modo privacidad se aplica a toda la aplicación desde la raíz.
  useEffect(() => {
    document.body.classList.toggle('ed-stealth', stealth);
  }, [stealth]);

  if (status === 'loading') {
    return <SplashScreen />;
  }

  return (
    <Routes>
      <Route path="/entrar" element={<LoginPage />} />
      <Route path="/crear-cuenta" element={<RegisterPage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Todo lo que ya funciona. */}
        <Route path="/" element={<DashboardPage />} />
        <Route path="/nuevo" element={<NewEntryPage />} />
        <Route path="/movimientos" element={<MovementsPage />} />
        <Route path="/cuentas" element={<AccountsPage />} />
        <Route path="/fiado" element={<FiadoPage />} />
        <Route path="/deudas" element={<DebtsPage />} />
        <Route path="/presupuestos" element={<BudgetsPage />} />
        <Route path="/recurrentes" element={<RecurringPage />} />
        <Route path="/compartidos" element={<SplitsPage />} />
        <Route path="/metas" element={<GoalsPage />} />
        <Route path="/informes" element={<ReportsPage />} />
        <Route path="/importar" element={<ImportPage />} />
        <Route path="/copiloto" element={<CopilotPage />} />
        <Route path="/recibos" element={<ReceiptsPage />} />
        <Route path="/ajustes" element={<SettingsPage />} />

        {/* Lo que queda por construir, con su explicación. */}
        {PENDING_ROUTES.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<ComingSoonPage title={route.title} detail={route.detail} />}
          />
        ))}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Pantalla de carga inicial. Evita el parpadeo entre «entrar» y «ya estás dentro». */
function SplashScreen(): React.JSX.Element {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-14 w-14 animate-pulse rounded-full border-2 border-ed-gold/40 border-t-ed-gold"
          aria-hidden="true"
        />
        <p className="text-sm text-ed-text-muted">Cargando ElDescuadre…</p>
      </div>
    </div>
  );
}
